import React, { useEffect, useState } from "react";
import { api, formatErr } from "@/lib/api";
import { toast } from "sonner";
import {
  UserPlus, Users, Shield, ShieldOff, KeyRound, Trash2, CheckCircle2, X,
  Loader2, Save, Sparkles,
} from "lucide-react";

function Field({ label, children, testid }) {
  return (
    <div data-testid={testid}>
      <label className="zfd-label">{label}</label>
      {children}
    </div>
  );
}

function PermissionCheckboxGrid({ catalog, selected, onChange, testidPrefix = "perm" }) {
  const has = (k) => selected.includes(k);
  const toggle = (k) => {
    if (has(k)) onChange(selected.filter((p) => p !== k));
    else onChange([...selected, k]);
  };
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-1.5" data-testid="perm-grid">
      {catalog.map((s) => (
        <label
          key={s.key}
          className={`flex items-center gap-2 rounded border px-3 py-2 text-xs cursor-pointer select-none transition ${has(s.key) ? "border-[#FBBF24]/50 bg-[#FBBF24]/8" : "border-white/8 hover:border-white/20 bg-black/25"}`}
          data-testid={`${testidPrefix}-${s.key}`}
        >
          <input
            type="checkbox"
            checked={has(s.key)}
            onChange={() => toggle(s.key)}
            className="accent-[#FBBF24]"
          />
          <div className="min-w-0">
            <div className="font-mono-num text-[10px] uppercase tracking-widest text-[#FBBF24]">{s.key}</div>
            <div className="text-white/70 text-xs truncate">{s.label}</div>
          </div>
        </label>
      ))}
    </div>
  );
}

function CreateForm({ catalog, onCreated }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ email: "", password: "", full_name: "", permissions: [] });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (form.permissions.length === 0) { toast.error("Grant at least one permission."); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/admin/sub-admins", form);
      toast.success(`Sub-admin created: ${data.email}`);
      setForm({ email: "", password: "", full_name: "", permissions: [] });
      setOpen(false);
      onCreated?.();
    } catch (e) { toast.error(formatErr(e)); }
    finally { setBusy(false); }
  };

  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        className="btn-deploy px-4 py-2.5 inline-flex items-center gap-2"
        data-testid="team-invite-open"
      >
        <UserPlus size={14} /> Invite Sub-Admin
      </button>
    );
  }
  return (
    <form onSubmit={submit} className="glass-card gold-border p-5 space-y-4" data-testid="team-invite-form">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.28em] text-[#FBBF24] font-bold">
          <UserPlus size={12} /> New Sub-Admin
        </div>
        <button type="button" onClick={() => setOpen(false)} className="text-white/50 hover:text-white" data-testid="team-invite-close"><X size={16} /></button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <Field label="Full name" testid="ta-name-wrap">
          <input className="zfd-input" required value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} placeholder="e.g. KYC Team Lead" data-testid="ta-name" />
        </Field>
        <Field label="Login email" testid="ta-email-wrap">
          <input className="zfd-input" required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="team@yourcompany.com" data-testid="ta-email" />
        </Field>
        <Field label="Initial password (min 8 chars)" testid="ta-pass-wrap">
          <input className="zfd-input" required type="password" minLength={8} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="Send this via a secure channel" data-testid="ta-password" />
        </Field>
      </div>
      <div>
        <div className="zfd-label mb-1">Permissions ({form.permissions.length} selected)</div>
        <PermissionCheckboxGrid catalog={catalog} selected={form.permissions} onChange={(perms) => setForm({ ...form, permissions: perms })} testidPrefix="ta-perm" />
      </div>
      <div className="flex items-center justify-end gap-2 pt-2 border-t border-white/10">
        <button type="button" onClick={() => setOpen(false)} className="text-white/60 hover:text-white text-xs uppercase tracking-widest">Cancel</button>
        <button type="submit" disabled={busy} className="btn-deploy px-4 py-2 inline-flex items-center gap-2 disabled:opacity-40" data-testid="ta-submit">
          {busy ? <Loader2 size={14} className="animate-spin" /> : <UserPlus size={14} />} Create Sub-Admin
        </button>
      </div>
    </form>
  );
}

function SubAdminRow({ sa, catalog, onChanged }) {
  const [editing, setEditing] = useState(false);
  const [perms, setPerms] = useState(sa.admin_permissions || []);
  const [busy, setBusy] = useState(false);

  const savePerms = async () => {
    if (perms.length === 0) { toast.error("A sub-admin must keep at least one permission."); return; }
    setBusy(true);
    try {
      const { data } = await api.patch(`/admin/sub-admins/${sa.id}`, { permissions: perms });
      toast.success("Permissions updated.");
      setEditing(false);
      onChanged?.(data);
    } catch (e) { toast.error(formatErr(e)); }
    finally { setBusy(false); }
  };
  const toggleActive = async () => {
    setBusy(true);
    try {
      const { data } = await api.patch(`/admin/sub-admins/${sa.id}`, { is_active: !sa.is_active });
      toast.success(!sa.is_active ? "Sub-admin re-activated." : "Sub-admin deactivated — session revoked.");
      onChanged?.(data);
    } catch (e) { toast.error(formatErr(e)); }
    finally { setBusy(false); }
  };
  const del = async () => {
    if (!confirm(`Permanently delete sub-admin "${sa.email}"? This can't be undone.`)) return;
    setBusy(true);
    try {
      await api.delete(`/admin/sub-admins/${sa.id}`);
      toast.success("Sub-admin removed.");
      onChanged?.(null, sa.id);
    } catch (e) { toast.error(formatErr(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className={`glass-card p-4 border ${sa.is_active ? "border-white/10" : "border-white/5 opacity-60"}`} data-testid={`sub-admin-row-${sa.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <Shield size={14} className={sa.is_active ? "text-[#FBBF24]" : "text-white/30"} />
            <div className="font-bold text-white truncate">{sa.full_name}</div>
            <span className={`text-[9px] uppercase tracking-widest font-black px-1.5 py-0.5 rounded ${sa.is_active ? "bg-emerald-500/15 text-emerald-300 border border-emerald-400/30" : "bg-white/5 text-white/50 border border-white/15"}`}>
              {sa.is_active ? "Active" : "Disabled"}
            </span>
          </div>
          <div className="text-xs text-white/55 mt-0.5">{sa.email}</div>
          <div className="text-[10px] text-white/35 mt-0.5">
            Created {sa.created_at?.split("T")[0]} · Last seen {sa.last_seen_at ? sa.last_seen_at.split("T")[0] : "—"}
          </div>
        </div>
        <div className="flex items-center gap-1">
          <button onClick={toggleActive} disabled={busy} className="p-2 rounded border border-white/10 hover:border-[#FBBF24]/50 hover:text-[#FBBF24]" title={sa.is_active ? "Deactivate" : "Reactivate"} data-testid={`sa-toggle-${sa.id}`}>
            {sa.is_active ? <ShieldOff size={14} /> : <Shield size={14} />}
          </button>
          <button onClick={del} disabled={busy} className="p-2 rounded border border-white/10 hover:border-[#FCA5A5] hover:text-[#FCA5A5]" title="Delete" data-testid={`sa-delete-${sa.id}`}>
            <Trash2 size={14} />
          </button>
        </div>
      </div>

      <div className="mt-3">
        {editing ? (
          <div className="space-y-3">
            <PermissionCheckboxGrid catalog={catalog} selected={perms} onChange={setPerms} testidPrefix={`sa-edit-${sa.id}`} />
            <div className="flex items-center justify-end gap-2">
              <button onClick={() => { setPerms(sa.admin_permissions || []); setEditing(false); }} className="text-white/60 hover:text-white text-xs">Cancel</button>
              <button onClick={savePerms} disabled={busy} className="btn-deploy px-3 py-1.5 inline-flex items-center gap-1.5 text-xs disabled:opacity-40" data-testid={`sa-save-${sa.id}`}>
                {busy ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />} Save
              </button>
            </div>
          </div>
        ) : (
          <div className="flex items-start gap-2 flex-wrap">
            {(sa.admin_permissions || []).map((p) => (
              <span key={p} className="text-[10px] uppercase tracking-widest font-bold text-[#FBBF24] bg-[#FBBF24]/10 border border-[#FBBF24]/30 px-2 py-0.5 rounded">{p}</span>
            ))}
            <button onClick={() => setEditing(true)} className="ml-auto text-[10px] uppercase tracking-widest text-white/55 hover:text-[#FBBF24] inline-flex items-center gap-1" data-testid={`sa-edit-${sa.id}`}>
              <KeyRound size={11} /> Edit Permissions
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default function AdminTeam() {
  const [catalog, setCatalog] = useState([]);
  const [subs, setSubs] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [c, s] = await Promise.all([
        api.get("/admin/permissions/catalog"),
        api.get("/admin/sub-admins"),
      ]);
      setCatalog(c.data.scopes);
      setSubs(s.data);
    } catch (e) { toast.error(formatErr(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const handleChanged = (updated, deletedId) => {
    if (deletedId) setSubs((prev) => prev.filter((s) => s.id !== deletedId));
    else if (updated) setSubs((prev) => prev.map((s) => (s.id === updated.id ? updated : s)));
    else load();
  };

  if (loading) return (
    <div className="text-white/50 flex items-center gap-2 p-6"><Loader2 size={16} className="animate-spin" /> Loading team…</div>
  );

  return (
    <div className="space-y-5" data-testid="admin-team">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.28em] text-[#FBBF24] font-bold">
            <Sparkles size={12} /> Team & Access Control
          </div>
          <h2 className="font-display text-2xl md:text-3xl font-black tracking-tight mt-1">
            Sub-Admins · {subs.length} teammate{subs.length === 1 ? "" : "s"}
          </h2>
          <p className="text-white/55 text-sm mt-1 max-w-2xl">
            Grant scoped access to future admins without exposing the full console. Only the super-admin can create,
            revoke, or re-scope teammates. Deactivating a sub-admin instantly revokes their session.
          </p>
        </div>
        <CreateForm catalog={catalog} onCreated={load} />
      </div>

      {subs.length === 0 ? (
        <div className="glass-card p-10 text-center text-white/55" data-testid="team-empty">
          <Users size={26} className="text-white/25 mx-auto mb-2" />
          No sub-admins yet. Click <strong className="text-[#FBBF24]">Invite Sub-Admin</strong> to add your first teammate.
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3" data-testid="team-grid">
          {subs.map((sa) => (
            <SubAdminRow key={sa.id} sa={sa} catalog={catalog} onChanged={handleChanged} />
          ))}
        </div>
      )}

      <div className="rounded-lg border border-white/10 bg-black/20 p-4 text-xs text-white/60" data-testid="team-explainer">
        <div className="text-[10px] uppercase tracking-[0.22em] text-[#FBBF24] font-bold mb-1 flex items-center gap-1">
          <CheckCircle2 size={11} /> How permissions work
        </div>
        <ul className="list-disc list-inside space-y-1 marker:text-[#FBBF24]/60">
          <li>Sub-admins log in via the same <code>/login</code> page and land on <code>/admin</code>.</li>
          <li>Their console tabs are filtered to only what you granted — the other tabs simply don&apos;t render.</li>
          <li>Backend endpoints double-check the scope on every request, so even a modified frontend can&apos;t bypass access.</li>
          <li>Deactivate revokes their JWT immediately (single-active-session enforcement kicks them off).</li>
        </ul>
      </div>
    </div>
  );
}
